<template>
    <a
        :href="url"
        target="_blank"
        :class="positionAlignment"
        class="lds-popup-banner"
        :style="widgetColors"
    >
        <div class="lds-popup-banner__img">
            <icon-advertising />
            <img
                :src='currentImage'
                alt="popup-banner"
            >

        </div>
        <div class="lds-popup-banner__text-content">
            <h4 class="lds-popup-banner__title">
                {{ widgetTitle }}
            </h4>
            <p class="lds-popup-banner__subtitle">
                {{ widgetSubtitle }}
            </p>
        </div>
        <div
            class="lds-popup-banner__btn"
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 15 12" fill="none">
                <path d="M8.75 0.203157L14.25 5.45316C14.4062 5.60941 14.5 5.79691 14.5 6.01566C14.5 6.20316 14.4062 6.39066 14.25 6.54691L8.75 11.7969C8.46875 12.0782 7.96875 12.0782 7.6875 11.7657C7.40625 11.4844 7.40625 10.9844 7.71875 10.7032L11.875 6.76566H1.25C0.8125 6.76566 0.5 6.42191 0.5 6.01566C0.5 5.57816 0.8125 5.26566 1.25 5.26566H11.875L7.71875 1.29691C7.40625 1.01566 7.40625 0.515657 7.6875 0.234407C7.96875 -0.0780928 8.4375 -0.0780928 8.75 0.203157Z"/>
            </svg>
            <span>
                {{ widgetButton }}
            </span>
        </div>
        <div class="lds-popup-banner__close" @click.prevent="$emit('closeWidget')">
            <icon-base
                width="18"
                height="18"
                icon-color="#A0A6BF"
            >
                <icon-close/>
            </icon-base>
        </div>
    </a>
</template>

<script setup>
import {computed, inject, ref, toRef } from 'vue';
import useThemes from './composables/useThemes.js';
import IconClose from '../../common/icons/IconClose.vue';
import IconBase from '../../common/icons/IconBase.vue';
import getS3Domain from '../../common/s3Domain.js';
import {useContainerWidth} from "../../composables/useContainerWidth";
import IconAdvertising from "../../common/icons/IconAdvertising.vue";

const props = defineProps({
    settings: {
        type: Object,
        default: () => {},
    },

    widgetInfo: {
        type: Object,
        default: () => {},
    },

    widgetTitle: {
        type: String,
        default: '',
    },

    widgetSubtitle: {
        type: String,
        default: '',
    },

    widgetButton: {
        type: String,
        default: '',
    },

    url: {
        type: String,
        default: '',
    },
});

const settings = toRef(props, 'settings');
const {widgetColors} = useThemes({settings});
const s3Domain = ref(getS3Domain());

const positionAlignment = computed(() => {
    return `lds-popup-banner_position_${props.settings.alignment}`;
});

const horizontalOffset = computed(() => {
    return props.settings.offsetHorizontal + 'px';
});

const verticalOffset = computed(() => {
    return props.settings.offsetVertical + 'px';
});

// todo заменить пути

const imageCollection = computed(() => {
    return props.settings.imageBannerMini ?
        {
            desktop: `${s3Domain.value}${props.settings.imageBannerMini}`,
            mobile: `${s3Domain.value}${props.settings.imageBannerMini}`,
        } :
        {
            desktop: `${s3Domain.value}banner_mini/image-popup-banner-design1-desktop.png`,
            mobile: `${s3Domain.value}banner_mini/image-popup-banner-design1-mobile.png`
        };
});

const currentImage = computed(() => {
    return containerWidth.value < 868 ? imageCollection.value.mobile : imageCollection.value.desktop;
});

// container queries
const widgetId = inject('widgetId');
const widgetType = inject('widgetType');
const widgetMode = inject('widgetMode');

const { containerWidth } = useContainerWidth(widgetId, widgetType, widgetMode);

</script>

<style lang="scss" scoped>
.lds-popup-banner {
    $this: &;

    position: fixed;

    display: flex;
    align-items: center;
    gap: 24px;
    padding: 16px 48px 16px 16px !important;
    box-sizing: border-box !important;

    background-color: var(--lw-widget-bg);
    border-radius: 24px !important;

    box-shadow: 0 10px 40px 0 rgba(0, 0, 0, 0.07);
    z-index: 100;

    font-weight: 500 !important;
    text-decoration: none !important;

    text-align: left;

    * {
        box-sizing: border-box !important;
        font-family: var(--lw-font-family), 'Arial', sans-serif !important;
        outline: none !important;
    }

    &_position {
        &_leftBottom {
            left: v-bind(horizontalOffset);
            bottom: v-bind(verticalOffset);
        }

        &_leftTop {
            left: v-bind(horizontalOffset);
            top: v-bind(verticalOffset);
        }

        &_rightBottom {
            right: v-bind(horizontalOffset);
            bottom: v-bind(verticalOffset);
        }

        &_rightTop {
            right: v-bind(horizontalOffset);
            top: v-bind(verticalOffset);
        }

    }

    &__text-content {
        max-width: 494px;
    }

    &__title {
        margin: 0 0 4px !important;

        font-size: 20px !important;
        font-style: normal;
        font-weight: 600 !important;
        line-height: 28px;

        color: var(--lw-title);
    }

    &__subtitle {
        margin: 0 !important;

        font-size: 14px !important;
        font-style: normal;
        font-weight: 500 !important;
        line-height: 28px;
        white-space: pre-line;

        color: var(--lw-text);
    }

    &__img {
        position: relative;
        img {
            width: 100px;
            height: 100px;
            object-fit: cover;
            border-radius: 16px !important;
        }
    }

    &__btn {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 10px;

        width: 72px;
        height: 72px;
        padding: 13px !important;

        border-radius: 70px !important;

        font-family: var(--lw-font-family), 'Arial', sans-serif;
        color: var(--lw-btn-color);

        background-color: var(--lw-btn-bg);
        box-shadow: var(--lw-btn-shadow);

        text-decoration: none;

        & svg {
            width: 24px;
            height: 20px;

            path {
                fill: var(--lw-btn-color);
            }
        }

        & span {
            display: none;
        }

        &:hover {
            background-color: var(--lw-btn-hover-bg);
            color: var(--lw-btn-hover-color);

            & svg path {
                fill: var(--lw-btn-hover-color);
            }
        }

        &:active {
            background-color: var(--lw-btn-active-bg);
            color: var(--lw-btn-active-color);

            & svg path {
                fill: var(--lw-btn-active-color);
            }
        }
    }

    &__close {
        position: absolute;
        right: 12px;
        top: 16px;
        z-index: 115;

        display: flex;
        align-items: center;
        justify-content: center;

        cursor: pointer;
    }

    @container (width < 868px) {
        flex-direction: column;
        padding: 16px !important;
        gap: 4px;
        max-width: 298px;

        &__text-content {
            max-width: 100%;
            margin-top: 20px !important;
        }

        &__img img {
            width: 266px;
            object-fit: cover;
        }

        &__btn {
            width: 100%;
            height: 100%;

            display: flex;
            padding: 15px 24px 17px 24px !important;
            justify-content: center;
            align-items: center;
            gap: 8px;
            align-self: stretch;

            border-radius: 16px !important;

            font-size: 16px !important;
            font-style: normal;
            font-weight: 500 !important;
            line-height: 24px;

            & svg {
                width: 15px;
                height: 12px;
            }

            & span {
                display: inline;
            }
        }

        &__close {
            top: 8px;
            right: 8px;

            width: 18px;
            height: 18px;
            padding: 0 3px !important;

            border-radius: 24px !important;
            background-color: var(--lw-widget-bg);
        }

        .widget-advertising-icon{
          top: 0;
        }
    }

    @media (max-width: 868px) {
        flex-direction: column;
        padding: 16px !important;
        gap: 4px;

        &__text-content {
            max-width: 266px;
            margin-top: 20px !important;
        }

        &__img img {
            width: 266px;
            object-fit: cover;
        }

        &__btn {
            width: 100%;
            height: 100%;

            display: flex;
            padding: 15px 24px 17px 24px !important;
            justify-content: center;
            align-items: center;
            gap: 8px;
            align-self: stretch;

            border-radius: 16px !important;

            font-size: 16px !important;
            font-style: normal;
            font-weight: 500 !important;
            line-height: 24px;

            & svg {
                width: 15px;
                height: 12px;
            }

            & span {
                display: inline;
            }
        }

        &__close {
            top: 8px;
            right: 8px;

            width: 18px;
            height: 18px;
            padding: 0 3px !important;

            border-radius: 24px !important;
            background-color: var(--lw-widget-bg);
        }

      .widget-advertising-icon{
        top: 0;
      }
    }
}
.widget-advertising-icon{
  position: absolute;
  bottom: 0;
  left: 0;
}
</style>
